import * as angular from 'angular';

import { ComponentDefinition, IUserModel, Application } from '../../../models';
import { IAuthorizationService, IUserService, IApplicationService, IRolesService, IStoreService } from '../../../services';
import { IRootScope } from 'IRootScope';
import { Roles } from '../../../common/';

class UserStateController implements angular.IComponentController {
    // Only for retro compatibility with userChangePassword (which will be updated soon)
    controlChangePassword: any;
    formController: angular.IFormController;
    hasSignature: boolean;
    imageStyle: any;
    isConnected: boolean;
    isEditMode: boolean;
    isLoading: boolean;
    isPictureLoaded: boolean;
    loadingMessage: string;
    roles = [Roles.administrator, Roles.master, Roles.engineer, Roles.operator, Roles.reader];
    user: IUserModel;
    userName: string;
    userApplicationRoles: {
        applicationName: string;
        roles: { [role: string]: boolean };
    }[];

    private _applications: Application[];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$q',
        'storeService',
        'authorizationService',
        'applicationService',
        'rolesService',
        'userService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: ng.ui.IStateService,
        private _$q: angular.IQService,
        private _storeService: IStoreService,
        private _authorizationService: IAuthorizationService,
        private _applicationService: IApplicationService,
        private _roleService: IRolesService,
        private _userService: IUserService
    ) {
    }

    $onInit() {
        this.userName = this._$state.params.userName;
        this._$state.current.data.breadcrumbs.level4 = this.userName;
        this.isEditMode = this._$rootScope.isEditMode;

        this._$scope.$watch(() => this._$rootScope.isEditMode, () => {
            this.isEditMode = this._$rootScope.isEditMode;
        });

        this.isConnected = this._authorizationService.isConnected();
        // get the package name by StateParams
        if (this.userName === undefined) {
            this.userName = this._$state.params.userName;
        }

        // get the package by its name
        if (!this.user && this.userName) {
            this.isLoading = true;
            this._userService
                .getUserModelByUserName(this.userName)
                .then((data) => {
                    this.user = data;
                    return this.refresh();
                })
                .finally(() => this.isLoading = false);
        }
        else {
            this.isLoading = true;
            this.refresh()
                .finally(() => {
                    this.isLoading = false;
                });
        }
    }

    private refresh() {
        if (this.user) {
            return this._$q.all([
                this.loadUserPicture(),
                this.loadUserSignature(),

                //  Build the table of application roles; should this be done here?
                this._$q.all([
                    this._applicationService.getForUserAsync(this.userName, false),
                    this._roleService.getAll()
                ])
                    .then(data => {
                        const rolesDic = {};
                        data[1].forEach(d => {
                            rolesDic[d.name] = d.id;
                        });
                        this.userApplicationRoles = data[0]
                            .map(dto => {
                                const dic = {};
                                this.roles.forEach(r => dic[r] = dto.roles[this.userName].indexOf(rolesDic[r]) >= 0);
                                return {
                                    applicationName: dto.application.name,
                                    roles: dic
                                };
                            });
                        this._applications = data[0].map(dto => dto.application);
                    })
            ]);
        }
        else {
            return this._$q.when({});
        }
    }

    loadUserPicture(): angular.IPromise<void> {
        return this._userService
            .getPicture(this.user.userName)
            .then(data => {
                (this.user as any).userPicture = data
                    ? { 'background-image': `url('data:image/false;base64,${data}')` }
                    : '';
                this.isPictureLoaded = true;
            });
    }

    loadUserSignature(): angular.IPromise<void> {
        return this._userService
            .getSignature(this.user.userName)
            .then(data => {
                (this.user as any).userSignature = data
                    ? { 'background-image': `url('data:image/false;base64,${data}')` }
                    : {};
            });
    }

    onApplicationClick(applicationName: string): void {
        const application = this._applications.filter(a => a.name === applicationName)[0];

        this._storeService.dispatchers().application.disconnect()
            .then(() => this._storeService.dispatchers().authentication.connectByApplicationName(applicationName, application.guid))
            .then(data => this._$state.go('home'));
    }
}

export const wtUserState: ComponentDefinition = {
    componentId: 'wtUserState',
    controller: UserStateController,
    template: require('./userState.component.html'),
    controllerAs: 'vm',
    bindings: {}
};